import React, { FC, useEffect, useMemo } from 'react';

import { certificatesActions, useCertificates, useSelectedCertificate } from 'Modules/business/cryptoprofiles';
import { useAction } from 'Modules/core/redux';
import { Button2, DropdownSelect, Refresh24, Stack } from 'Modules/ui3';
import { useTranslation } from 'Modules/core/i18n';


interface CertSelectorData {
    data: any;
    value: string;
    label: string;
    disabled: boolean;
}


const useCertifacatesDropdownOptions = () => {
    const certificates = useCertificates('sign');

    return useMemo(() => {
        if (!Array.isArray(certificates))
            return [];
        return certificates.map<CertSelectorData>(
            data => ({
                data,
                value: data.thumbprint,
                label: data.name,
                disabled: data.disabled,
            }),
        ).filter(certOption => !certOption.disabled);
    }, [certificates]);
};


export const UKEPSelector: FC = () => {
    const { t } = useTranslation('UKEPSelector');

    const setStoreSelectedCert = useAction<CertSelectorData>(certData => certificatesActions.setCertificate('sign', { cert: certData?.value }));
    const updateCertList = useAction(certificatesActions.updateList);
    const clearSelectedCertificate = useAction(certificatesActions.setCertificate);

    const selectedCertificate = useSelectedCertificate('sign');
    const certList = useCertifacatesDropdownOptions();

    useEffect(
        () => {
            updateCertList('sign', {});
        },
        [clearSelectedCertificate, updateCertList],
    );

    const textSelector = certList.find(({ value }) => value === selectedCertificate)?.label ?? t('chooseCert');

    return (
        <Stack
            direction='row'
            spacing={4}
        >
            <DropdownSelect<CertSelectorData>
                data-testid='cert-name'
                text={textSelector}
                options={certList}
                selected={selectedCertificate}
                onSelect={setStoreSelectedCert}
                clearable
                popperProps={{
                    disablePortal: true,
                    sx: {
                        width: '100%',
                    },
                    dropdownSx: {
                        p: 0,
                    },
                }}
                buttonSx={{
                    height: '100%',
                }}
                wrapperButtonSx={{
                    width: '100%',
                }}
            />

            <Button2
                variant='stroke'
                sx={{
                    width: '56px',
                    height: '56px',
                    minWidth: '56px',
                }}
                onClick={() => updateCertList('sign', {})}
            >
                <Refresh24
                    sx={{
                        width: '24px',
                        height: '24px',
                    }}
                />
            </Button2>
        </Stack>
    );
};
